// Comprehensive pricing configuration system for all locations and services

export interface ServicePricing {
  airport_transfer: number;
  local_taxi: number;
  group_transfer: number;
  executive_service: number;
}

export interface LocationPricing {
  location_slug: string;
  location_name: string;
  parent_city: "Leicester" | "Birmingham";
  services: ServicePricing;
  last_updated: string;
  updated_by: string;
}

export interface RegionalPricing {
  leicester_base: ServicePricing;
  birmingham_base: ServicePricing;
  default_markup: number; // Percentage markup for smaller towns/villages
}

// Default pricing configuration
export const defaultPricing: RegionalPricing = {
  leicester_base: {
    airport_transfer: 25,
    local_taxi: 8,
    group_transfer: 45,
    executive_service: 35,
  },
  birmingham_base: {
    airport_transfer: 20,
    local_taxi: 8,
    group_transfer: 40,
    executive_service: 30,
  },
  default_markup: 10, // 10% markup for smaller locations
};

// Individual location pricing overrides
const locationPricingOverrides: Record<string, Partial<ServicePricing>> = {
  // Leicester area - premium locations
  leicester: {
    airport_transfer: 25,
    local_taxi: 8,
    group_transfer: 45,
    executive_service: 35,
  },
  loughborough: {
    airport_transfer: 28,
    local_taxi: 10,
    group_transfer: 48,
    executive_service: 38,
  },

  // Birmingham area - premium locations
  birmingham: {
    airport_transfer: 20,
    local_taxi: 8,
    group_transfer: 40,
    executive_service: 30,
  },
  solihull: {
    airport_transfer: 18,
    local_taxi: 9,
    group_transfer: 38,
    executive_service: 32,
  },
  "sutton-coldfield": {
    airport_transfer: 22,
    local_taxi: 9,
    group_transfer: 42,
    executive_service: 33,
  },

  // Smaller locations get automatic markup
  enderby: {
    airport_transfer: 28, // Leicester base + 12% for village
  },
  groby: {
    airport_transfer: 28,
  },
  aldridge: {
    airport_transfer: 23, // Birmingham base + 15% for smaller town
  },
};

// Generate pricing for all locations
export const generateLocationPricing = (): LocationPricing[] => {
  const allLocations = [
    // Leicester area
    {
      slug: "leicester",
      name: "Leicester",
      parent: "Leicester" as const,
      type: "city",
    },
    {
      slug: "loughborough",
      name: "Loughborough",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "coalville",
      name: "Coalville",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "hinckley",
      name: "Hinckley",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "market-harborough",
      name: "Market Harborough",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "melton-mowbray",
      name: "Melton Mowbray",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "ashby-de-la-zouch",
      name: "Ashby-de-la-Zouch",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "lutterworth",
      name: "Lutterworth",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "oadby",
      name: "Oadby",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "wigston",
      name: "Wigston",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "enderby",
      name: "Enderby",
      parent: "Leicester" as const,
      type: "village",
    },
    {
      slug: "syston",
      name: "Syston",
      parent: "Leicester" as const,
      type: "town",
    },
    {
      slug: "anstey",
      name: "Anstey",
      parent: "Leicester" as const,
      type: "village",
    },
    {
      slug: "groby",
      name: "Groby",
      parent: "Leicester" as const,
      type: "village",
    },
    {
      slug: "kibworth",
      name: "Kibworth",
      parent: "Leicester" as const,
      type: "village",
    },

    // Birmingham area
    {
      slug: "birmingham",
      name: "Birmingham",
      parent: "Birmingham" as const,
      type: "city",
    },
    {
      slug: "solihull",
      name: "Solihull",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "sutton-coldfield",
      name: "Sutton Coldfield",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "west-bromwich",
      name: "West Bromwich",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "dudley",
      name: "Dudley",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "walsall",
      name: "Walsall",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "wolverhampton",
      name: "Wolverhampton",
      parent: "Birmingham" as const,
      type: "city",
    },
    {
      slug: "smethwick",
      name: "Smethwick",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "wednesbury",
      name: "Wednesbury",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "tipton",
      name: "Tipton",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "bilston",
      name: "Bilston",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "willenhall",
      name: "Willenhall",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "aldridge",
      name: "Aldridge",
      parent: "Birmingham" as const,
      type: "town",
    },
    {
      slug: "coventry",
      name: "Coventry",
      parent: "Birmingham" as const,
      type: "city",
    },
    {
      slug: "tamworth",
      name: "Tamworth",
      parent: "Birmingham" as const,
      type: "town",
    },
  ];

  return allLocations.map((location) => {
    const basePricing =
      location.parent === "Leicester"
        ? defaultPricing.leicester_base
        : defaultPricing.birmingham_base;

    // Apply overrides if they exist
    const overrides = locationPricingOverrides[location.slug] || {};

    // Apply markup for smaller locations if no specific override
    const markup =
      location.type === "village" ? 0.15 : location.type === "town" ? 0.05 : 0;

    const finalPricing: ServicePricing = {
      airport_transfer:
        overrides.airport_transfer ||
        Math.round(basePricing.airport_transfer * (1 + markup)),
      local_taxi:
        overrides.local_taxi ||
        Math.round(basePricing.local_taxi * (1 + markup)),
      group_transfer:
        overrides.group_transfer ||
        Math.round(basePricing.group_transfer * (1 + markup)),
      executive_service:
        overrides.executive_service ||
        Math.round(basePricing.executive_service * (1 + markup)),
    };

    return {
      location_slug: location.slug,
      location_name: location.name,
      parent_city: location.parent,
      services: finalPricing,
      last_updated: new Date().toISOString(),
      updated_by: "system",
    };
  });
};

// Helper functions for pricing management
export const getPricingForLocation = (
  locationSlug: string,
): LocationPricing | null => {
  const allPricing = generateLocationPricing();
  return allPricing.find((p) => p.location_slug === locationSlug) || null;
};

export const updateLocationPricing = (
  locationSlug: string,
  newPricing: Partial<ServicePricing>,
  updatedBy: string = "admin",
): LocationPricing => {
  const current = getPricingForLocation(locationSlug);
  if (!current) {
    throw new Error(`Location ${locationSlug} not found`);
  }

  return {
    ...current,
    services: { ...current.services, ...newPricing },
    last_updated: new Date().toISOString(),
    updated_by,
  };
};

export const bulkUpdateRegionalPricing = (
  region: "Leicester" | "Birmingham",
  newBasePricing: Partial<ServicePricing>,
  applyToAll: boolean = true,
  updatedBy: string = "admin",
): LocationPricing[] => {
  const allPricing = generateLocationPricing();
  const regionLocations = allPricing.filter((p) => p.parent_city === region);

  if (!applyToAll) {
    // Only update base pricing, don't apply to individual locations
    if (region === "Leicester") {
      defaultPricing.leicester_base = {
        ...defaultPricing.leicester_base,
        ...newBasePricing,
      };
    } else {
      defaultPricing.birmingham_base = {
        ...defaultPricing.birmingham_base,
        ...newBasePricing,
      };
    }
    return regionLocations;
  }

  // Apply to all locations in the region
  return regionLocations.map((location) => ({
    ...location,
    services: { ...location.services, ...newBasePricing },
    last_updated: new Date().toISOString(),
    updated_by,
  }));
};

export const formatPrice = (price: number): string => {
  return `£${price}`;
};

export const formatPriceFromText = (priceText: string): string => {
  const price = parseInt(priceText.replace(/[£,]/g, ""));
  return `From ${formatPrice(price)}`;
};

// Get pricing display for components
export const getServicePriceDisplay = (
  locationSlug: string,
  serviceType: keyof ServicePricing,
): string => {
  const pricing = getPricingForLocation(locationSlug);
  if (!pricing) return "From £25"; // Default fallback

  return formatPriceFromText(pricing.services[serviceType].toString());
};

// Export pricing summary for analytics
export const getPricingSummary = () => {
  const allPricing = generateLocationPricing();
  const leicesterPricing = allPricing.filter(
    (p) => p.parent_city === "Leicester",
  );
  const birminghamPricing = allPricing.filter(
    (p) => p.parent_city === "Birmingham",
  );

  return {
    total_locations: allPricing.length,
    leicester_locations: leicesterPricing.length,
    birmingham_locations: birminghamPricing.length,
    average_airport_transfer: Math.round(
      allPricing.reduce((sum, p) => sum + p.services.airport_transfer, 0) /
        allPricing.length,
    ),
    average_local_taxi: Math.round(
      allPricing.reduce((sum, p) => sum + p.services.local_taxi, 0) /
        allPricing.length,
    ),
    price_range: {
      airport_transfer: {
        min: Math.min(...allPricing.map((p) => p.services.airport_transfer)),
        max: Math.max(...allPricing.map((p) => p.services.airport_transfer)),
      },
      local_taxi: {
        min: Math.min(...allPricing.map((p) => p.services.local_taxi)),
        max: Math.max(...allPricing.map((p) => p.services.local_taxi)),
      },
    },
  };
};
